// RoleActions — the role's ONE registry and its overlays (ADR-036 §7 — the four
// kinds; the exemplar is `pages/teams/TeamActions.tsx`). Roles management is
// admin-only (no permission key), so every row is gated `'admin'`.
//   • `roleActs` is the PURE registry — the shape test
//     (`components/patterns/actionGrammar.test.ts`) walks it.
//   • `useRoleActions` resolves it for a role and returns the ACTS every surface
//     composes from — the peek's top row + door through `ContextPanel`, the
//     page's seat + door through `EntityHeader`, the Details card's act through
//     `SectionAct` — and the OVERLAYS (the record drawer, the two confirms),
//     mounted ONCE by the caller beside its surface.
// The acts: Edit capabilities — the PRIMARY, a ROUTE to the focused capability
// editor (the seat and the peek's main button navigate; its one home — the
// Capabilities card carries no link) · Edit role — the Details card's drawer
// (name · description · the inactivity lock) · Clone — an operation: routes to
// the create wizard PREFILLED through router state rather than a silent POST,
// so the admin renames and reviews before committing (sidestepping the
// "(copy)" name collision a blind clone-twice would hit; a handler, since the
// route carries state) · Disable / Reactivate — the lifecycle; a disable
// attempt surfaces ROLE_IN_USE (the role is assigned to members) as the
// dialog's inline error through errors.json. One string per act: the row's
// word is the card's link and the drawer's title.

import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { RoleDetail } from '@ems-app/schemas/roles/responses';
import { resolveActs, type ActionItem, type EntityAct } from '../../components/patterns/entityRegistry.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { icons } from '../../components/ui/icons.js';
import { useCan } from '../../hooks/useCan.js';
import { useEntitlements } from '../../hooks/useEntitlements.js';
import { useSetRoleDisabled } from '../../hooks/useRoleMutations.js';
import { RoleDetailsDrawer } from './RoleDetailsDrawer.js';
import { roleStatus, type RoleStatus } from './roleDisplay.js';
import type { RoleCloneState } from './roleForm.js';

/** What each act does — the hook wires these to its overlays and routes. */
export interface RoleActHandlers {
  edit: () => void;
  clone: () => void;
  disable: () => void;
  reactivate: () => void;
}

/** The facts the rows read beside the status. */
export interface RoleActFacts {
  /** The focused capability editor's route — the primary navigates there. */
  capabilitiesRoute: string;
}

/** The role's registry — pure: the rows, their kinds, sections and predicates. */
export function roleActs(h: RoleActHandlers, facts: RoleActFacts): ActionItem<RoleStatus>[] {
  return [
    // The primary — the act people come for: the capability editor (a route). Its one home is the seat / the
    // peek's main button; the Capabilities card carries no link.
    { id: 'edit-capabilities', kind: 'primary', labelKey: 'actions.editCapabilities', icon: icons.edit, run: { route: facts.capabilitiesRoute }, gate: 'admin', order: 10 },
    // The one edit — the Details card's drawer.
    { id: 'edit', kind: 'edit', section: 'details', labelKey: 'actions.editRole', icon: icons.edit, run: { handler: h.edit }, gate: 'admin', order: 10 },
    { id: 'clone', kind: 'operation', labelKey: 'actions.clone', icon: icons.copy, run: { handler: h.clone }, gate: 'admin', order: 10 },
    // The lifecycle — the door; the status flips the pair.
    { id: 'reactivate', kind: 'lifecycle', labelKey: 'actions.reactivate', icon: icons.restore, run: { handler: h.reactivate }, gate: 'admin', order: 10, show: (ctx) => ctx.status === 'disabled' },
    { id: 'disable', kind: 'lifecycle', labelKey: 'actions.disable', icon: icons.statusBlocked, run: { handler: h.disable }, gate: 'admin', order: 10, show: (ctx) => ctx.status !== 'disabled' },
  ];
}

export interface RoleActions {
  /** The resolved set — what the shells and the cards compose from. */
  acts: EntityAct[];
  /** Every dialog and drawer, mounted ONCE by the caller beside its surface. */
  overlays: ReactNode;
}

export function useRoleActions(projectId: string, role: RoleDetail): RoleActions {
  const { t } = useTranslation('roles');
  const navigate = useNavigate();
  const can = useCan(projectId);
  const entitlements = useEntitlements(projectId);
  const setDisabled = useSetRoleDisabled(projectId);
  const [editOpen, setEditOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [reactivateOpen, setReactivateOpen] = useState(false);

  const cloneTo = () => {
    const clone: RoleCloneState = {
      name: t('form.cloneSuffix', { name: role.name }),
      description: role.description ?? '',
      inactivity_timeout_min: role.inactivity_timeout_min,
      grants: role.permissions,
      managed_roles: role.managed_roles,
      responsibility_levels: role.responsibility_levels,
    };
    navigate(`/p/${projectId}/roles/new`, { state: { clone } });
  };

  const acts = resolveActs(
    roleActs(
      { edit: () => setEditOpen(true), clone: cloneTo, disable: () => setDisableOpen(true), reactivate: () => setReactivateOpen(true) },
      { capabilitiesRoute: `/p/${projectId}/roles/${role.id}/capabilities` },
    ),
    { can, entitlements, status: roleStatus(role) },
    (key) => t(key),
  );

  const overlays = (
    <>
      {/* The record's drawer stays mounted (it slides) — keyed by the role so a
          peek→peek switch never keeps the previous role's seed (the stale-edit rule). */}
      {can('admin') && <RoleDetailsDrawer key={role.id} projectId={projectId} role={role} open={editOpen} onClose={() => setEditOpen(false)} />}
      <ConfirmDialog
        open={disableOpen}
        onClose={() => setDisableOpen(false)}
        tone="danger"
        title={t('disable.title', { name: role.name })}
        description={t('disable.body')}
        confirmLabel={t('disable.confirm')}
        onConfirm={async () => {
          await setDisabled.mutateAsync({ roleId: role.id, disabled: true });
        }}
      />
      <ConfirmDialog
        open={reactivateOpen}
        onClose={() => setReactivateOpen(false)}
        tone="default"
        title={t('reactivate.title', { name: role.name })}
        description={t('reactivate.body')}
        confirmLabel={t('reactivate.confirm')}
        onConfirm={async () => {
          await setDisabled.mutateAsync({ roleId: role.id, disabled: false });
        }}
      />
    </>
  );

  return { acts, overlays };
}
